import { HfsMissionSettingsActionEnum } from 'fleet-data-model';
import { UpdateMissionTypeEnum } from '~/domain/roboticmower-details/models/UpdateMissionTypeEnum';
import { type Operation, OperationStatus } from '../models';
import { type Fields } from '../store/hfs-operations/reducer';

const hfsOperationsStatusFlowMap = [
  'Active',
  // 'Sent' suppressed for now
  [
    // Can be delivered in independent order
    'Done',
    'Completed',
  ],
  'Success',
];

const getHfsOperationStatusPosition = (status: string) => {
  return hfsOperationsStatusFlowMap.findIndex((level) =>
    typeof level === 'string' ? level === status : level.includes(status)
  );
};

const hfsOperationsAnytimeStatuses = new Set(['Error', 'Timeout']);

export const importHfsOperationStatus = (externalStatus: string, operation: Operation) => {
  if (hfsOperationsAnytimeStatuses.has(externalStatus)) {
    // No additional check needed
    return externalStatus;
  }

  const localStatus = operation.status;

  if (operation.supportedStates !== undefined) {
    if (
      operation.supportedStates.indexOf(externalStatus) >
      operation.supportedStates.indexOf(localStatus)
    ) {
      return externalStatus;
    }
    return localStatus;
  }

  const localPosition = getHfsOperationStatusPosition(localStatus);
  const importedPosition = getHfsOperationStatusPosition(externalStatus);
  if (importedPosition > localPosition) {
    return externalStatus;
  }
  return localStatus;
};

export const filterWsUpdatesPendingHfsOperations = (operations: Operation[]) => {
  return operations.filter(
    (operation) => operation.operationId !== undefined && checkIfOperationIsActive(operation)
  );
};

export const checkIfOperationIsActive = (operation: Operation) => {
  if (checkIfOperationIsFailed(operation)) {
    return false;
  }
  if (operation.supportedStates) {
    return (
      operation.supportedStates.at(-1) !== operation.status &&
      operation.status !== OperationStatus.Acknowledged
    );
  }
  return (
    OperationStatus.Active === operation.status || OperationStatus.Posting === operation.status
  );
};

export const checkIfOperationIsFailed = (operation: Operation) => {
  return [
    OperationStatus.Failed,
    OperationStatus.Timeout,
    OperationStatus.Rejected,
    OperationStatus.Error,
  ].includes(operation.status);
};
export const checkHfsActionMatch = (operation: Operation, updateHfsActionId: string) => {
  const hfsActionsOperationPrefixes = new Map();
  hfsActionsOperationPrefixes.set('MOWER_UPDATE_MISSION', 'UPDATE_MISSION/');
  hfsActionsOperationPrefixes.set(
    HfsMissionSettingsActionEnum.MOWER_AREA_CUTTING_HEIGHT,
    UpdateMissionTypeEnum.UpdateMissionCuttingHeight
  );
  hfsActionsOperationPrefixes.set(
    HfsMissionSettingsActionEnum.MOWER_AREA_SCHEDULE,
    UpdateMissionTypeEnum.UpdateMissionSchedule
  );
  hfsActionsOperationPrefixes.set(
    HfsMissionSettingsActionEnum.MOWER_AREA_AUGMENT_BY_VISION,
    UpdateMissionTypeEnum.UpdateMissionAugmentByVision
  );
  if (
    hfsActionsOperationPrefixes.has(updateHfsActionId) &&
    operation.hfsActionId.startsWith(hfsActionsOperationPrefixes.get(updateHfsActionId))
  ) {
    return true;
  }
  return operation.hfsActionId === updateHfsActionId;
};
export const reflectOperationCompletion = (state: Fields, operationId: string) => {
  state.mowingBehaviourSettings = state.mowingBehaviourSettings.map((item) =>
    item.operationId === operationId
      ? {
          ...item,
          completionDate: new Date(),
        }
      : item
  );
  state.missionEnabled = state.missionEnabled.map((item) =>
    item.operationId === operationId
      ? {
          ...item,
          completionDate: new Date(),
        }
      : item
  );
  state.missionAugmentByVision = state.missionAugmentByVision.map((item) =>
    item.operationId === operationId
      ? {
          ...item,
          completionDate: new Date(),
        }
      : item
  );
};
export const reflectOperationError = (state: Fields, operationId: string) => {
  state.mowingBehaviourSettings = state.mowingBehaviourSettings.filter(
    (item) => item.operationId !== operationId
  );
  state.missionEnabled = state.missionEnabled.filter((item) => item.operationId !== operationId);
  state.missionAugmentByVision = state.missionAugmentByVision.filter(
    (item) => item.operationId !== operationId
  );
  state.mowerSchedule = state.mowerSchedule.filter((item) => item.operationId !== operationId);
  state.mowerCuttingHeight = state.mowerCuttingHeight.filter(
    (item) => item.operationId !== operationId
  );
  state.missionCuttingHeight = state.missionCuttingHeight.filter(
    (item) => item.operationId !== operationId
  );
  state.missionSchedule = state.missionSchedule.filter((item) => item.operationId !== operationId);
};
